import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { type Team, type Event } from '../types';
import { useTranslation } from 'react-i18next';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="bg-gray-800 rounded-lg p-5 shadow-lg">
        <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
        {children}
    </section>
);

const TeamDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();

    const [team, setTeam] = useState<Team | null>(null);
    const [matches, setMatches] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        let mounted = true;

        const load = async () => {
            try {
                const [teamRes, matchesRes] = await Promise.all([
                    api.get(`/Teams/${id}`),
                    api.get(`/Teams/${id}/matches`),
                ]);
                if (!mounted) return;
                setTeam(teamRes.data);
                setMatches(matchesRes.data || []);
            } catch (e) {
                console.error('Błąd pobierania szczegółów drużyny:', e);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, [id]);

    if (loading) return <div className="text-center text-white">{t('common.loading')}</div>;
    if (!team) return <div className="text-center text-red-300">{t('teamsPage.teamNotFound') || 'Nie znaleziono drużyny.'}</div>;

    const anyTeam = team as any;
    const clubInfo: string = anyTeam.clubInfo || '';
    const achievements: string[] = Array.isArray(anyTeam.achievements) ? anyTeam.achievements : [];
    const players: { name: string; isCaptain?: boolean }[] = Array.isArray(anyTeam.players) ? anyTeam.players : [];

    return (
        <div className="max-w-5xl mx-auto p-4 md:p-6 bg-gray-900 text-white space-y-6">
            {/* header */}
            <div className="bg-gray-800 rounded-lg p-5 shadow-lg flex gap-4 items-start">
                {team.logoUrl ? (
                    <img
                        src={team.logoUrl}
                        alt={`${team.name} logo`}
                        className="w-24 h-24 rounded-md object-cover bg-gray-700"
                        onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                ) : (
                    <div className="w-24 h-24 rounded-md bg-gray-700 flex items-center justify-center text-gray-400">
                        LOGO
                    </div>
                )}
                <div className="flex-1">
                    <h1 className="text-3xl font-bold">{team.name}</h1>
                    {clubInfo && (
                        <p className="mt-3 text-gray-300 whitespace-pre-line">{clubInfo}</p>
                    )}
                </div>
            </div>

            {/* Zawodnicy */}
            <Section title={t('teamsPage.players') || 'Zawodnicy'}>
                {players.length === 0 ? (
                    <p className="text-gray-400">{t('teamsPage.noPlayers') || 'Brak zawodników.'}</p>
                ) : (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 list-disc list-inside text-gray-200">
                        {players.map((p, i) => (
                            <li key={i}>
                                {p.name} {p.isCaptain ? <span className="ml-2 text-xs px-2 py-0.5 rounded bg-indigo-600/30 text-indigo-300 align-middle">C</span> : null}
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            {/* Sukcesy */}
            <Section title={t('teamsPage.successes') || 'Sukcesy'}>
                {achievements.length === 0 ? (
                    <p className="text-gray-400">{t('teamsPage.noSuccesses') || 'Brak wpisanych sukcesów.'}</p>
                ) : (
                    <ul className="list-disc list-inside text-gray-200 space-y-1">
                        {achievements.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                )}
            </Section>

            {/* Archiwum meczy */}
            <Section title={t('teamsPage.matchesArchive') || 'Archiwum meczy'}>
                {matches.length === 0 ? (
                    <p className="text-gray-400">{t('teamsPage.noMatches') || 'Brak meczów w archiwum.'}</p>
                ) : (
                    <div className="space-y-3">
                        {matches.map(m => (
                            <Link to={`/event/${m.id}`} key={m.id} className="block bg-gray-900 rounded-md p-3 hover:bg-gray-800 transition-colors">
                                <div className="flex justify-between items-center">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-white truncate">{m.eventName}</p>
                                        <p className="text-sm text-gray-400 truncate">{m.teamAName} vs {m.teamBName}</p>
                                    </div>
                                    <div className="text-right text-sm text-gray-300">
                                        <p>{new Date(m.startTime).toLocaleDateString('pl-PL')}</p>
                                        <p>{new Date(m.startTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
};

export default TeamDetailPage;
